import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  ROLLING_DAYS_TYPES,
  supportsRollingDays,
} from 'constants/DynamicAvailability.constants';
import { compact, keyBy } from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultAPIState,
} from 'types/state.interface';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  deleteEntityFromState,
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
} from 'utils/sliceHelpers';
import {
  EDynamicAvailabilityType,
  ETimeScope,
  type TDynamicAvailability,
} from '@timeedit/types';
import api from '../services/api.service';
import { startAppListening } from './listenerMiddleware';

const TYPE_SPECIFIC_FIELDS = [
  'maxReservationLength',
  'maxReservationCount',
  'minBreakLength',
  'minBreakCount',
  'unitCount',
] as const;

// Which of the type-specific fields each availability type actually uses;
// the rest are stripped before the API call so stale values left over from
// other types never reach the server
const FIELDS_BY_TYPE: Record<
  EDynamicAvailabilityType,
  readonly (typeof TYPE_SPECIFIC_FIELDS)[number][]
> = {
  [EDynamicAvailabilityType.FREE_TIME]: ['minBreakLength', 'minBreakCount'],
  [EDynamicAvailabilityType.TOTAL_HOURS]: ['maxReservationLength', 'unitCount'],
  [EDynamicAvailabilityType.CONSECUTIVE_HOURS]: [
    'maxReservationLength',
    'minBreakLength',
  ],
  [EDynamicAvailabilityType.TOTAL_RESERVATIONS]: [
    'maxReservationCount',
    'unitCount',
  ],
};

export type TDynamicAvailabilityState =
  IDefaultAPIState<TDynamicAvailability> & {
    appliedRules: { [typeId: number]: IDefaultAPIResult<TDynamicAvailability> };
    firstDynamicAvailability?: TDynamicAvailability;
    secondDynamicAvailability?: TDynamicAvailability;
    selectedDynamicAvailabilityIds: number[];
  };

export const initialState: TDynamicAvailabilityState = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
  appliedRules: {},
  firstDynamicAvailability: undefined,
  secondDynamicAvailability: undefined,
  selectedDynamicAvailabilityIds: [],
};

const slice = createSlice({
  name: 'dynamicAvailability',
  initialState,
  reducers: {
    fetchDynamicAvailabilitiesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchDynamicAvailabilitiesSuccess: (
      state,
      { payload }: PayloadAction<TDynamicAvailability[]>
    ) => {
      state.results = payload;
      state.map = keyBy(payload, 'id');
      finishedLoadingSuccess(state);
    },
    fetchDynamicAvailabilitiesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createDynamicAvailability: (
      state,
      { payload: type }: PayloadAction<EDynamicAvailabilityType>
    ) => {
      const newDynamicAvailability: TDynamicAvailability = {
        id: 0,
        name: ``,
        description: '',
        dynamicAvailabilityType: type,
        scopeType: ETimeScope.DAILY,
        includeAbstractObjects: false,
        // Rolling days defaults to 1 (= limit applies per single day)
        ...(ROLLING_DAYS_TYPES.includes(type) && { unitCount: 1 }),
      };

      state.firstDynamicAvailability = newDynamicAvailability;
      state.results = state.results.filter((da) => da.id !== 0);
      state.results.unshift(newDynamicAvailability);
      state.map[0] = newDynamicAvailability;
      finishedLoadingSuccess(state);
    },
    saveDynamicAvailabilityRequest: (state) => {
      isLoadingRequest(state);
    },
    saveDynamicAvailabilitySuccess: (
      state,
      {
        payload: { isFirstDynamicAvailability },
      }: PayloadAction<{ isFirstDynamicAvailability: boolean }>
    ) => {
      if (isFirstDynamicAvailability) {
        state.firstDynamicAvailability = undefined;
      } else {
        state.secondDynamicAvailability = undefined;
      }
      finishedLoadingSuccess(state);
    },
    saveDynamicAvailabilityFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteDynamicAvailabilityRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteDynamicAvailabilitySuccess: (
      state,
      {
        payload: { dynamicAvailabilityId },
      }: PayloadAction<{ dynamicAvailabilityId: number }>
    ) => {
      deleteEntityFromState(dynamicAvailabilityId, state);
      if (dynamicAvailabilityId === state?.firstDynamicAvailability?.id) {
        state.firstDynamicAvailability = undefined;
      }
      if (dynamicAvailabilityId === state?.secondDynamicAvailability?.id) {
        state.secondDynamicAvailability = undefined;
      }
      finishedLoadingSuccess(state);
    },
    deleteDynamicAvailabilityFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstDynamicAvailability: (state: any, { payload }) => {
      state.firstDynamicAvailability = payload;
      if (!payload || (payload && payload.id !== 0)) {
        state.results = state.results.filter(
          (da: TDynamicAvailability) => da.id !== 0
        );
        state.map[0] = undefined;
      }
    },
    setSecondDynamicAvailability: (state: any, { payload }) => {
      state.secondDynamicAvailability = payload;
    },
    duplicateDynamicAvailability: (state: any, { payload }) => {
      const newDynamicAvailability: TDynamicAvailability = {
        ...payload,
        id: 0,
        name: `${payload.name}-copy`,
        history: undefined,
      };
      state.firstDynamicAvailability = newDynamicAvailability;
      state.results = state.results.filter(
        (da: TDynamicAvailability) => da.id !== 0
      );
      state.results.unshift(newDynamicAvailability);
      state.map[0] = newDynamicAvailability;
    },
    setSelectedDynamicAvailabilityIds: (
      state,
      { payload }: PayloadAction<number[]>
    ) => {
      state.selectedDynamicAvailabilityIds = payload;
    },
  },
});

export default slice.reducer;

export const dynamicAvailabilityLoading = (state: TRootState) =>
  state.dynamicAvailability.loading;
export const dynamicAvailabilitySelector = (state: TRootState) =>
  state.dynamicAvailability.results;
export const dynamicAvailabilityMapSelector = (state: TRootState) =>
  state.dynamicAvailability.map;
export const firstDynamicAvailabilitySelector = (state: TRootState) =>
  state.dynamicAvailability.firstDynamicAvailability;
export const secondDynamicAvailabilitySelector = (state: TRootState) =>
  state.dynamicAvailability.secondDynamicAvailability;
export const selectedDynamicAvailabilityIdsSelector = (state: TRootState) =>
  state.dynamicAvailability.selectedDynamicAvailabilityIds;

export const {
  fetchDynamicAvailabilitiesRequest,
  fetchDynamicAvailabilitiesSuccess,
  fetchDynamicAvailabilitiesFailure,
  createDynamicAvailability,
  deleteDynamicAvailabilityRequest,
  deleteDynamicAvailabilityFailure,
  deleteDynamicAvailabilitySuccess,
  saveDynamicAvailabilityRequest,
  saveDynamicAvailabilitySuccess,
  saveDynamicAvailabilityFailure,
  setFirstDynamicAvailability,
  setSecondDynamicAvailability,
  setSelectedDynamicAvailabilityIds,
  duplicateDynamicAvailability,
} = slice.actions;

export const fetchDynamicAvailabilities = createAppAsyncThunk(
  'dynamicAvailability/fetchDynamicAvailabilities',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchDynamicAvailabilitiesRequest());
      const { results } = await api.get({
        endpoint: `/dynamic-availability`,
      });
      dispatch(fetchDynamicAvailabilitiesSuccess(results));
    } catch (e) {
      dispatch(fetchDynamicAvailabilitiesFailure());
      console.error(e);
    }
  }
);

export const saveDynamicAvailability = createAppAsyncThunk<
  void,
  {
    isFirstDynamicAvailability: boolean;
    dynamicAvailability: TDynamicAvailability;
  }
>(
  'dynamicAvailability/saveDynamicAvailability',
  async ({ isFirstDynamicAvailability, dynamicAvailability }, { dispatch }) => {
    try {
      dispatch(saveDynamicAvailabilityRequest());
      /**
       * Could be both a create new or an update
       * Depending on which, we need to use different methods (POST or PATCH)
       */
      const preppedDynamicAvailability = prepEntityForAPICall(
        dynamicAvailability,
        true,
        true
      );
      delete preppedDynamicAvailability.history;
      const type = dynamicAvailability.dynamicAvailabilityType;
      for (const field of TYPE_SPECIFIC_FIELDS) {
        if (!FIELDS_BY_TYPE[type].includes(field)) {
          delete preppedDynamicAvailability[field];
        }
      }
      // Rolling days only applies to daily Max available hours / reservations
      if (!supportsRollingDays(type, dynamicAvailability.scopeType)) {
        delete preppedDynamicAvailability.unitCount;
      }
      // The API rejects explicit nulls (Joi number validation); omitting the
      // fields means "cleared" — the server defaults them
      for (const field of [
        'scopeBeginTime',
        'scopeEndTime',
        'minBreakLength',
      ] as const) {
        if (preppedDynamicAvailability[field] === null) {
          delete preppedDynamicAvailability[field];
        }
      }
      if (dynamicAvailability.id === 0) {
        // Create a new one
        await api.post({
          endpoint: `/dynamic-availability`,
          data: preppedDynamicAvailability,
        });
        dispatch(
          saveDynamicAvailabilitySuccess({ isFirstDynamicAvailability })
        );
        dispatch(fetchDynamicAvailabilities());
      } else {
        await api.patch({
          endpoint: `/dynamic-availability/${dynamicAvailability.id}`,
          data: preppedDynamicAvailability,
        });
        dispatch(
          saveDynamicAvailabilitySuccess({ isFirstDynamicAvailability })
        );
        dispatch(fetchDynamicAvailabilities());
      }
    } catch (error) {
      dispatch(saveDynamicAvailabilityFailure());
      console.error(error);
    }
  }
);

export const deleteDynamicAvailability = createAppAsyncThunk<void, number>(
  'dynamicAvailability/deleteDynamicAvailability',
  async (dynamicAvailabilityId, { dispatch }) => {
    try {
      dispatch(deleteDynamicAvailabilityRequest());
      /**
       * No need to send the request to the backend if the entity hasn't been saved
       */
      if (dynamicAvailabilityId !== 0)
        await api.delete({
          endpoint: `/dynamic-availability/${dynamicAvailabilityId}`,
        });
      dispatch(
        deleteDynamicAvailabilitySuccess({
          dynamicAvailabilityId,
        })
      );
    } catch (e) {
      dispatch(deleteDynamicAvailabilityFailure());
      console.error(e);
    }
  }
);

export const checkDisable =
  (idFirstDynamicAvailability: boolean): TAppThunk<boolean> =>
  (dispatch, getStore) => {
    const { firstDynamicAvailability, secondDynamicAvailability, map } =
      getStore().dynamicAvailability;

    const drawer = idFirstDynamicAvailability
      ? firstDynamicAvailability
      : secondDynamicAvailability;

    return (
      !drawer || !map[drawer.id] || checkSavedDisabled(drawer, map[drawer.id])
    );
  };
export const getDrawer =
  (idFirstDynamicAvailability: boolean): TAppThunk<TDynamicAvailability> =>
  (dispatch, getStore) => {
    const { firstDynamicAvailability, secondDynamicAvailability } =
      getStore().dynamicAvailability;

    return idFirstDynamicAvailability
      ? firstDynamicAvailability
      : secondDynamicAvailability;
  };

startAppListening({
  predicate: (_, currentState, prevState) =>
    currentState.dynamicAvailability.firstDynamicAvailability?.id !==
      prevState.dynamicAvailability.firstDynamicAvailability?.id ||
    currentState.dynamicAvailability.secondDynamicAvailability?.id !==
      prevState.dynamicAvailability.secondDynamicAvailability?.id,
  effect: (_, { dispatch, getState }) => {
    const {
      dynamicAvailability: {
        firstDynamicAvailability,
        secondDynamicAvailability,
      },
    } = getState();
    dispatch(
      setSelectedDynamicAvailabilityIds(
        compact([firstDynamicAvailability?.id, secondDynamicAvailability?.id])
      )
    );
  },
});
